export enum CustomizationFieldType {
  TEXT = 'text',
  FILE_UPLOAD = 'file upload',
  IMAGE_SELECTION = 'image selection',
}

export interface CustomizationField {
  label: string;
  type: CustomizationFieldType;
}

export interface CustomizationData {
  [fieldLabel: string]: string; // Field label -> value (text or file URL)
}

/**
 * Parse customization fields from comma-separated string
 * Format: "Label (type), Label 2 (type), ..."
 * Example: "Glass 1 (text), Glass 2 (text), Upload logo (file upload)"
 */
export function parseCustomizationFields(fieldsString: string | undefined): CustomizationField[] {
  if (!fieldsString || fieldsString.trim() === '') {
    return [];
  }

  return fieldsString
    .split(',')
    .map((field) => {
      const trimmed = field.trim();
      // Match pattern: "Label (type)"
      const match = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);

      if (!match) {
        return null;
      }

      const label = match[1]?.trim();
      const typeStr = match[2]?.trim().toLowerCase();

      if (!label || !typeStr) {
        return null;
      }

      // Map type string to enum
      let type: CustomizationFieldType;
      if (typeStr === 'text') {
        type = CustomizationFieldType.TEXT;
      } else if (typeStr === 'file upload') {
        type = CustomizationFieldType.FILE_UPLOAD;
      } else if (typeStr === 'image selection') {
        type = CustomizationFieldType.IMAGE_SELECTION;
      } else {
        return null; // Unknown type
      }

      return { label, type };
    })
    .filter((field): field is CustomizationField => field !== null);
}

/**
 * Format customization data for display
 * Returns formatted string like:
 * "- Glass 1: John Doe\n- Glass 2: Jane Smith\n- Upload logo: https://..."
 */
export function formatCustomizationData(data: CustomizationData | undefined): string {
  if (!data || Object.keys(data).length === 0) {
    return '';
  }

  return Object.entries(data)
    .map(([label, value]) => {
      // For file URLs, show shortened version
      if (value.startsWith('http')) {
        return `- ${label}: [Uploaded file]`;
      }
      return `- ${label}: ${value}`;
    })
    .join('\n');
}
